import React, { useEffect, useRef, useState } from 'react';
import { useFitStore } from '../../store/useFitStore';
import { startStripeConnect } from '../../lib/billing';

const STATUS_LABEL: Record<string, string> = {
  active: 'Connected · ready to charge clients',
  pending: 'Connected · finishing setup in Stripe',
  restricted: 'Connected · action needed in Stripe',
  none: 'Not connected',
};
const STATUS_COLOR: Record<string, string> = {
  active: 'var(--status-green)',
  pending: 'var(--status-yellow)',
  restricted: 'var(--status-red)',
  none: 'var(--text-muted)',
};

/* Coach-only: link your own Stripe account (Standard Connect, hosted
   onboarding) so you can bill clients in-app or from your Stripe dashboard.
   Onboarding opens in a new tab; the account.updated webhook writes the live
   status, which we re-read when the coach comes back to this tab. */
const StripeConnectCard: React.FC = () => {
  const { user, refreshSelf, showToast } = useFitStore();
  const [busy, setBusy] = useState(false);
  const openedOnboarding = useRef(false);

  const status = user?.stripeConnectStatus ?? (user?.stripeAccountId ? 'active' : 'none');
  const connected = Boolean(user?.stripeAccountId);

  // After onboarding was opened in another tab, returning focus here means the
  // coach may have finished — pull the fresh Connect status.
  useEffect(() => {
    const onFocus = () => { if (openedOnboarding.current) refreshSelf(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshSelf]);

  const connect = async () => {
    setBusy(true);
    const res = await startStripeConnect();
    setBusy(false);
    if (!res.ok) {
      showToast(res.error || 'Could not start Stripe onboarding', 'error');
      return;
    }
    openedOnboarding.current = true;
    window.open(res.url, '_blank', 'noopener');
  };

  return (
    <div className="rounded-fit-md border border-fit-line p-4">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <p className="text-[14px] font-bold">Payments</p>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-fit-text-secondary">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLOR[status] ?? 'var(--text-muted)' }} />
          {STATUS_LABEL[status] ?? 'Not connected'}
        </span>
      </div>
      <p className="text-[12px] text-fit-text-secondary leading-relaxed mb-3">
        Connect your own Stripe account to charge clients. Payments go straight to your
        Stripe balance — FitFlex never holds your funds.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        {status !== 'active' && (
          <button onClick={connect} disabled={busy} className="btn btn-primary btn-sm disabled:opacity-40">
            {busy ? 'Opening Stripe…' : connected ? 'Finish Stripe setup' : 'Connect Stripe'}
          </button>
        )}
        {connected && (
          <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" className="btn btn-glass btn-sm" style={{ textDecoration: 'none' }}>
            Open my Stripe dashboard ↗
          </a>
        )}
        {connected && status !== 'active' && (
          <button onClick={() => refreshSelf()} className="text-[11px] text-fit-muted hover:text-white underline underline-offset-2">
            Refresh status
          </button>
        )}
      </div>
    </div>
  );
};

export default StripeConnectCard;
